/**
 * NodeActionToolbar Component
 *
 * Toolbar with customizable actions for selected nodes.
 * Shows entity name label + action buttons (first 3 visible) + menu for remaining.
 *
 * All styling via MUI theme - NO hardcoded colors.
 * Zoom-independent via ReactFlow NodeToolbar.
 */

import React, { useState, useCallback } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import MoreVert from '@mui/icons-material/MoreVert';

import { NodeAction } from '../../types/node-actions';
import { ActionsMenu } from './ActionsMenu';

export interface NodeActionToolbarProps {
  /** Entity ID (for callbacks) */
  entityId: string;

  /** Entity name (displayed as label) */
  entityName: string;

  /** Customizable action list */
  actions: NodeAction[];

  /** Callback when action is clicked */
  onActionClick: (entityId: string, actionKey: string) => void;
}

/**
 * NodeActionToolbar - Action toolbar for selected nodes
 *
 * Features:
 * - First 3 actions as IconButtons (visible)
 * - Remaining actions in dropdown menu (if >3 actions)
 * - Entity name label above toolbar
 * - Theme-only colors (no hardcoded values)
 * - Zoom-independent sizing
 */
export const NodeActionToolbar: React.FC<NodeActionToolbarProps> = ({
  entityId,
  entityName,
  actions,
  onActionClick,
}) => {
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const menuOpen = Boolean(menuAnchorEl);

  // First 3 actions as visible icons
  const visibleActions = actions.slice(0, 3);

  // All actions in menu (if >3 total)
  const showMenu = actions.length > 3;

  const handleActionClick = useCallback(
    (actionKey: string) => {
      onActionClick(entityId, actionKey);
    },
    [entityId, onActionClick]
  );

  const handleMenuOpen = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      setMenuAnchorEl(event.currentTarget);
    },
    []
  );

  const handleMenuClose = useCallback(() => {
    setMenuAnchorEl(null);
  }, []);

  const handleMenuActionClick = useCallback(
    (actionKey: string) => {
      handleActionClick(actionKey);
    },
    [handleActionClick]
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
      }}
      onClick={(e) => e.stopPropagation()} // Prevent node selection on toolbar click
    >
      {/* Entity name label */}
      <Typography
        variant="caption"
        sx={{
          textAlign: 'center',
          fontWeight: 500,
          color: 'text.secondary',
          px: 1,
        }}
        noWrap
      >
        {entityName}
      </Typography>

      {/* Action buttons */}
      <Box
        sx={{
          display: 'flex',
          gap: 0.5,
          padding: 1,
          backgroundColor: 'background.paper',
          borderRadius: 1,
          boxShadow: 3,
          border: 1,
          borderColor: 'divider',
        }}
      >
        {/* Visible action icons */}
        {visibleActions.map((action) => (
          <Tooltip key={action.key} title={action.title} arrow>
            <span>
              <IconButton
                size="small"
                color={action.color || 'default'}
                disabled={action.disabled}
                data-testid={`action-${action.key}`}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!action.disabled) {
                    handleActionClick(action.key);
                  }
                }}
                sx={{
                  '&:hover': {
                    backgroundColor:
                      action.color === 'error'
                        ? 'error.light'
                        : 'action.hover',
                  },
                }}
              >
                {action.icon}
              </IconButton>
            </span>
          </Tooltip>
        ))}

        {/* Menu button (if >3 actions) */}
        {showMenu && (
          <Tooltip title="More actions" arrow>
            <IconButton
              size="small"
              onClick={handleMenuOpen}
              data-testid="more-actions-btn"
              sx={{
                color: 'text.secondary',
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
              <MoreVert />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Dropdown menu */}
      {showMenu && (
        <ActionsMenu
          anchorEl={menuAnchorEl}
          open={menuOpen}
          onClose={handleMenuClose}
          actions={actions}
          onActionClick={handleMenuActionClick}
        />
      )}
    </Box>
  );
};
